import { createContext, useContext, useEffect, useRef, useState } from "react";

const PlayerCtx = createContext(null);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);

  const current = index >= 0 ? queue[index] : null;

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.play().catch(() => {/* keep user intent; audio may be CORS-blocked */});
    } else {
      audioRef.current.pause();
    }
  }, [playing, index]);

  const playTrack = (track, list = null) => {
    let q = list ?? [track];
    let i = q.findIndex((t) => t.id === track.id);
    if (i < 0) { q = [track]; i = 0; }
    setQueue(q);
    setIndex(i);
    setPlaying(true);
  };

  const toggle = () => {
    if (!current) return;
    setPlaying((p) => !p);
  };

  const next = () => {
    if (index < queue.length - 1) setIndex(index + 1);
  };

  const prev = () => {
    if (index > 0) setIndex(index - 1);
  };

  const seek = (sec) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = sec;
    setProgress(sec);
  };

  return (
    <PlayerCtx.Provider value={{ current, queue, playing, progress, duration, volume, setVolume, playTrack, toggle, next, prev, seek }}>
      {children}
      <audio
        ref={audioRef}
        src={current?.audio_url}
        onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || (current?.duration_sec ?? 0))}
        onEnded={() => {
          if (index < queue.length - 1) setIndex(index + 1);
          else setPlaying(false);
        }}
        crossOrigin="anonymous"
        preload="metadata"
      />
    </PlayerCtx.Provider>
  );
}

export const usePlayer = () => useContext(PlayerCtx);
